import api from './api';

const BASE       = '/paycards';
const ROOMS_BASE = '/rooms';

const payCardOpsService = {

  // ── Lookup student by QR token or numeric student ID ────────────────────
  async lookupStudent(idStr) {
    const clean = idStr.trim().toUpperCase();
    const isQr  = clean.startsWith('OMK|');
    const url   = isQr
      ? `${BASE}/lookup?qr=${encodeURIComponent(clean)}`
      : `${BASE}/lookup?studentId=${idStr.trim()}`;
    const res = await api.get(url);
    const d = res.data;
    return {
      payCardId:   d.payCardId,
      cardId:      d.payCardId,
      admissionId: d.admissionId,
      studentId:   d.studentId,
      studentName: d.studentName,
      bedCode:     d.roomCode,
      balance:     parseFloat(d.balance),
      status:      d.status,
      photoUrl:    d.photoUrl,
    };
  },

  // ── Canteen: charge wallet ───────────────────────────────────────────────
  async chargeCanteen(payCardId, cartItems) {
    // cartItems shape: { name, qty, price }
    const total   = cartItems.reduce((s, i) => s + i.qty * (i.price || 0), 0);
    const descArr = cartItems.map(i => `${i.name} x${i.qty}`);
    const res = await api.post(`${BASE}/charge`, {
      payCardId,
      category:    'CANTEEN',
      amount:      total,
      description: descArr.join(', '),
      operatorName: 'Staff',
    });
    return {
      transactionId: res.data.transactionId,
      newBalance:    parseFloat(res.data.balanceAfter),
      category:      res.data.category,
      timestamp:     res.data.timestamp,
    };
  },

  // ── Laundry: charge wallet ───────────────────────────────────────────────
  async chargeLaundry(payCardId, items) {
    const total   = items.reduce((s, i) => s + i.amount, 0);
    const descArr = items.map(i => `${i.name}(${i.svc.toUpperCase()} x${i.qty})`);
    const res = await api.post(`${BASE}/charge`, {
      payCardId,
      category:    'LAUNDRY',
      amount:      total,
      description: descArr.join(', '),
      operatorName: 'Staff',
    });
    return {
      transactionId: res.data.transactionId,
      newBalance:    parseFloat(res.data.balanceAfter),
      category:      res.data.category,
      timestamp:     res.data.timestamp,
    };
  },

  // ── Rooms: get all rooms in a tower/floor ───────────────────────────────
  async getRoomsByFloor(tower, floor) {
    const res = await api.get(`${ROOMS_BASE}/tower/${tower}/floor/${floor}`);
    // Returns floor info with rooms list
    return res.data?.rooms || res.data || [];
  },

  async getAllRooms() {
    const res = await api.get(`${ROOMS_BASE}/stats`);
    return res.data;
  },

  // ── Electricity: lookup beds for a room ──────────────────────────────────
  async getRoomBeds(roomCode) {
    const res = await api.get(`${ROOMS_BASE}/beds?roomCode=${encodeURIComponent(roomCode)}`);
    return res.data;
  },

  // ── Electricity: charge individual bed ───────────────────────────────────
  async chargeElectricityBed(payCardId, amount, roomCode, billingMonth) {
    const res = await api.post(`${BASE}/charge`, {
      payCardId,
      category:    'ELECTRICITY',
      amount,
      description: `Electricity ${roomCode} ${billingMonth}`,
      operatorName: 'Staff',
    });
    return {
      transactionId: res.data.transactionId,
      newBalance:    parseFloat(res.data.balanceAfter),
    };
  },

  // ── Electricity: bulk charge all beds in a room ──────────────────────────
  async chargeElectricityBulk({ roomCode, billingMonth, totalBill, charges, operatorName }) {
    const res = await api.post(`${BASE}/charge/electricity`, {
      roomCode,
      billingMonth,
      totalBill,
      charges,  // each item: { payCardId, bedCode, amount }
      operatorName: operatorName || 'Staff',
    });
    return res.data;
  },

  // ── Electricity: get billing history for a room ───────────────────────────
  async getElectricityHistory(roomCode) {
    const res = await api.get(`${BASE}/electricity/history?roomCode=${encodeURIComponent(roomCode)}`);
    return res.data;
  },

  // ── Electricity: check if room+month already billed ──────────────────────
  async checkDuplicateBill(roomCode, billingMonth) {
    const res = await api.get(
      `${BASE}/electricity/check-duplicate?roomCode=${encodeURIComponent(roomCode)}&billingMonth=${encodeURIComponent(billingMonth)}`
    );
    return res.data;
  },

  // ── Top-up wallet ─────────────────────────────────────────────────────────
  async topup(payCardId, amount, paymentMode = 'CASH', referenceId = null, operatorName = 'Staff') {
    const res = await api.post(`${BASE}/topup`, {
      payCardId, amount, operatorName, paymentMode, referenceId,
    });
    return {
      newBalance:    parseFloat(res.data.balanceAfter),
      transactionId: res.data.transactionId,
    };
  },

  // ── QR Management: get all students with pay cards ───────────────────────
  async getStudentQRList() {
    const res = await api.get(`${BASE}/list`);
    return res.data;
  },

  // ── QR Management: regenerate QR for a pay card ──────────────────────────
  async regenerateQR(payCardId) {
    const res = await api.post(`${BASE}/${payCardId}/regenerate-qr`);
    return {
      newQrToken: res.data.newQrToken,
      message:    res.data.message,
    };
  },

  // ── Transaction history for a pay card ───────────────────────────────────
  async getTransactions(payCardId) {
    const res = await api.get(`${BASE}/${payCardId}/transactions`);
    return res.data;
  },

  // ── Canteen menu management ───────────────────────────────────────────────
  async getCanteenItems() {
    const res = await api.get(`${BASE}/canteen/items`);
    return res.data;
  },

  async addCanteenItem(item) {
    const payload = { name: item.name, emoji: item.emoji || '🍽', price: Number(item.price), isActive: true };
    const res = await api.post(`${BASE}/canteen/items`, payload);
    return res.data;
  },

  async updateCanteenItem(id, item) {
    const payload = { name: item.name, emoji: item.emoji || '🍽', price: Number(item.price), isActive: true };
    const res = await api.put(`${BASE}/canteen/items/${id}`, payload);
    return res.data;
  },

  async deleteCanteenItem(id) {
    await api.delete(`${BASE}/canteen/items/${id}`);
  },

  async saveAllCanteenItems(items) {
    const res = await api.put(`${BASE}/canteen/items`, items);
    return res.data;
  },

  // ── Laundry rates management ──────────────────────────────────────────────
  async getLaundryRates() {
    const res = await api.get(`${BASE}/laundry/rates`);
    // Backend returns { wash, press, both } — return as-is
    return res.data;
  },

  async saveLaundryRates(rates) {
    const payload = { wash: Number(rates.wash) || 30, press: Number(rates.press) || 15, both: Number(rates.both) || 40 };
    const res = await api.put(`${BASE}/laundry/rates`, payload);
    return res.data;
  },
};

export default payCardOpsService;